import { apiService } from "../../../../../components/api/apiService";

export interface ChatAgentApiResponse {
  id: string;
  tenant_id: string;
  agent_type: string;
  agent_id: string;
  languages: string[];
  title: string;
  description: string;
  icon_path: string;
  roles: string[];
  llm_id: string;
  fo_url: string;
  chat_type: string;
  created_by: string;
  created_at: string;
}

export interface CreateChatAgentPayload {
  agent_type: string;
  agent_id: string;
  languages: string[];
  title: string;
  description: string;
  icon_path: string;
  roles: string[];
  llm_id: string;
  fo_url: string;
  chat_type: string;
  created_by: string;
  tenant_id: string;
}

// Core Setting DTO
export interface CoreSettingDto {
  id?: string;
  agent_id: string;
  tenant_id?: string;
  agent_title: string;
  description: string;
  agent_type: string;
  chat_type: string;
  language_model?: string;
  agent_icon_url?: string;
  languages?: string[];
}

// Knowledge Base DTO
export interface KnowledgeBaseDto {
  id?: string;
  knowledge_base_id?: string;
  agent_id: string;
  kb_name: string;
  kb_size_mb?: number;
  enable_filters?: boolean;
  public_access?: boolean;
}

// Intent DTO
export interface IntentDto {
  id?: string;
  intent_id?: string;
  agent_id: string;
  intent_name: string;
  intent_description?: string;
  graph_config?: string;
  is_active?: boolean;
}

// Department DTO
export interface DepartmentDto {
  id?: string;
  department_id?: string;
  tenant_id?: string;
  department_name: string;
  description?: string;
  created_by: string;
}

// Role DTO
export interface RoleDto {
  id?: string;
  role_id?: string;
  tenant_id?: string;
  role_name: string;
  description?: string;
  permissions?: string;
  created_by: string;
}

// User DTO
export interface UserDto {
  id?: string;
  user_id?: string;
  tenant_id?: string;
  department_id?: string;
  role_id?: string;
  username: string;
  email: string;
  is_active?: boolean;
  created_by: string;
}

// Deployment DTO (matches Swagger: add/update chat agent deployment)
export interface DeploymentDto {
  id?: string;
  deployment_id?: string;
  chatagent_master_id?: string;
  agent_id: string;
  tenant_id?: string;
  deployment_name: string;
  environment: string;
  deployment_url?: string;
  status: string;
  deployment_type?: string;
  base_domain?: string;
  url_path?: string;
  custom_domain?: string;
  direct_url?: string;
  website_embed_origin?: string;
  active_days?: string[];
  active_start_time?: string;
  active_end_time?: string;
  timezone?: string;
  heading_text?: string;
  welcome_message?: string;
  chat_token?: string;
  deployed_by?: string;
}

// Delete Request DTO
export interface DeleteRequestDto {
  id: string;
  entity_type: string;
}

// Agent List Item
export interface AgentListItemDto {
  id: string;
  agent_id: string;
  agent_title: string;
  agent_type: string;
  chat_type: string;
  created_at?: string;
  updated_at?: string;
}

// Helper DTOs
export interface HelperLLMDto {
  id: string;
  tenantId: string;
  provider: string;
  endpointUrl: string;
  modelName: string;
  region?: string;
  isDefault: boolean;
  isActive: boolean;
  costPreference?: string;
  maxTokens?: number;
  temperature?: number;
  topP?: number;
  apiVersion?: string;
  lastUsedAt?: string;
}

export interface HelperKnowledgeBaseDto {
  id: string;
  tenantId: string;
  knowledgeEntityID: string;
  title: string;
  description?: string;
  collectionNamespace: string;
  createdOn: string;
  tags?: string;
  documentCount: number;
  roleAccessList?: string[];
  userAccessList?: string[];
  allowedDepartmentIds?: string[];
}

export interface HelperDepartmentDto {
  id: string;
  department_id: string;
  tenant_id: string;
  department_name: string;
  description?: string;
  created_at?: string;
}

export const chatAgentService = {
  // ========================================
  // EXISTING ENDPOINTS
  // ========================================
  getChatAgents: () =>
    apiService.get('/api/ChatAgents/list', {}, 'backend'),

  createChatAgent: (payload: CreateChatAgentPayload) =>
    apiService.post('/api/ChatAgents/create', payload, 'backend'),

  updateChatAgent: (id: string, agentData: any) =>
    apiService.put(`/api/ChatAgents/${id}`, agentData, 'backend'),

  deleteChatAgent: (id: string) =>
    apiService.delete(`/api/ChatAgents/${id}`, 'backend'),

  // ========================================
  // CORE SETTINGS
  // ========================================
  upsertCoreSetting: (payload: CoreSettingDto) =>
    apiService.post('/api/ChatAgent/core-setting/upsert', payload, 'backend'),

  getAgentList: () =>
    apiService.get('/api/ChatAgent/agents/list', {}, 'backend'),

  getAgentById: (id: string) =>
    apiService.get(`/api/ChatAgent/agent/${id}`, {}, 'backend'),

  // ========================================
  // KNOWLEDGE BASE
  // ========================================
  upsertKnowledgeBase: (payload: KnowledgeBaseDto) =>
    apiService.post('/api/ChatAgent/knowledge-base/upsert', payload, 'backend'),

  getKnowledgeBasesByAgent: (agent_id: string) =>
    apiService.get(`/api/ChatAgent/knowledge-base/agent/${agent_id}`, {}, 'backend'),

  // ========================================
  // INTENTS
  // ========================================
  upsertIntent: (payload: IntentDto) =>
    apiService.post('/api/ChatAgent/intent/upsert', payload, 'backend'),

  getIntentsByAgent: (agent_id: string) =>
    apiService.get(`/api/ChatAgent/intent/agent/${agent_id}`, {}, 'backend'),

  // ========================================
  // SECURITY
  // ========================================
  upsertDepartment: (payload: DepartmentDto) =>
    apiService.post('/api/ChatAgent/security/department/upsert', payload, 'backend'),

  upsertRole: (payload: RoleDto) =>
    apiService.post('/api/ChatAgent/security/role/upsert', payload, 'backend'),

  upsertUser: (payload: UserDto) =>
    apiService.post('/api/ChatAgent/security/user/upsert', payload, 'backend'),

  getDepartmentsByTenant: () =>
    apiService.get(`/api/ChatAgent/security/departments`, {}, 'backend'),

  getRolesByTenant: () =>
    apiService.get(`/api/ChatAgent/security/roles`, {}, 'backend'),

  getUsersByTenant: () =>
    apiService.get(`/api/ChatAgent/security/users`, {}, 'backend'),

  // ========================================
  // DEPLOYMENT (backend – add/update chat agent deployment per Swagger)
  // ========================================
  upsertDeployment: (payload: DeploymentDto) =>
    apiService.post('/api/ChatAgent/deployment/upsert', payload, 'backend'),

  getDeploymentsByAgent: (agent_id: string) =>
    apiService.get(`/api/ChatAgent/deployment/agent/${agent_id}`, {}, 'backend'),

  // ========================================
  // DELETE
  // ========================================
  deleteEntity: (payload: DeleteRequestDto) =>
    apiService.post('/api/ChatAgent/delete', payload, 'backend'),

  // ========================================
  // FEATURE HELPERS
  // ========================================
  getActiveLLMs: () =>
    apiService.get('/api/FeatureHelper/llms', {}, 'backend'),

  getKnowledgeBases: () =>
    apiService.get('/api/FeatureHelper/knowledge-bases', {}, 'backend'),

  getDepartments: () =>
    apiService.get('/api/FeatureHelper/departments', {}, 'backend'),
};

export default chatAgentService;


// import { apiService } from "../../../../../components/api/apiService";

 

// export interface ChatAgentApiResponse {
//   id: string;
//   tenant_id: string;
//   agent_type: string;
//   agent_id: string;
//   languages: string[];
//   title: string;
//   description: string;
//   icon_path: string;
//   roles: string[];
//   llm_id: string;
//   fo_url: string;
//   chat_type: string;
//   created_by: string;
//   created_at: string;
// }

// export interface CreateChatAgentPayload {
//   agent_type: string;
//   agent_id: string;
//   languages: string[];
//   title: string;
//   description: string;
//   icon_path: string;
//   roles: string[];
//   llm_id: string;
//   fo_url: string;
//   chat_type: string;
//   created_by: string;
//   tenant_id: string;
// }

// export const chatAgentService = {
//   // Get all chat agents
//   getChatAgents: () =>
//     apiService.get('/api/ChatAgents/list', {}, 'backend'),

//   // Create a new chat agent
//   createChatAgent: (payload: CreateChatAgentPayload) =>
//     apiService.post('/api/ChatAgents/create', payload, 'backend'),

//   updateChatAgent: (id: string, agentData: any) =>
//     apiService.put(`/api/ChatAgents/${id}`, agentData, 'backend'),

//   deleteChatAgent: (id: string) =>
//     apiService.delete(`/api/ChatAgents/${id}`, 'backend'),

// };

// export default chatAgentService;